import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

export interface ChatSession {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  is_pinned?: boolean;
  pinned_at?: string | null;
}

interface ChatSessionsContextValue {
  sessions: ChatSession[];
  pinnedSessions: ChatSession[];
  unpinnedSessions: ChatSession[];
  loading: boolean;
  error: string | null;
  refreshSessions: () => Promise<void>;
  updateTitle: (id: number, title: string) => Promise<void>;
  deleteSession: (id: number) => Promise<void>;
  togglePin: (id: number) => Promise<void>;
}

const ChatSessionsContext = createContext<ChatSessionsContextValue | undefined>(undefined);

const sortSessions = (raw: ChatSession[]) => {
  const pinned = raw.filter(s => s.is_pinned);
  const unpinned = raw.filter(s => !s.is_pinned);

  pinned.sort((a, b) => {
    const dA = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
    const dB = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
    return dB - dA;
  });

  unpinned.sort((a, b) =>
    new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  return { pinned, unpinned };
};

export const ChatSessionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token, refreshAccessToken } = useAuth();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [pinnedSessions, setPinnedSessions] = useState<ChatSession[]>([]);
  const [unpinnedSessions, setUnpinnedSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const api_url = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
  // Keep effectiveEmail only to detect when to trigger fetch (user or admin present)
  // Customer sessions only: the admin console has no chat-session list, and an admin token gets a 401 from the
  // customer endpoint (which then triggered a pointless token refresh on every admin page).
  const effectiveEmail = user?.email ?? null;
  const authHeader = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  const applySessions = (raw: ChatSession[]) => {
    const { pinned, unpinned } = sortSessions(raw);
    setSessions(raw);
    setPinnedSessions(pinned);
    setUnpinnedSessions(unpinned);
  };

  const refreshSessions = useCallback(async () => {
    if (!effectiveEmail) return;
    setLoading(true);
    setError(null);
    try {
      let response = await fetch(
        `${api_url}/api/v1/chat/sessions?skip=0&limit=100`,
        { method: 'GET', headers: authHeader() }
      );

      // The access token expires after ~60 min; the cookie-based refresh only
      // runs on initial page load, so a long-lived tab hits 401s mid-session
      // ("Failed to load chat sessions"). Refresh once and retry before failing.
      if (response.status === 401) {
        try {
          await refreshAccessToken();
          const freshToken = window.localStorage.getItem('policyora_token');
          response = await fetch(
            `${api_url}/api/v1/chat/sessions?skip=0&limit=100`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(freshToken ? { Authorization: `Bearer ${freshToken}` } : {}),
              },
            }
          );
        } catch {
          setError('Your session has expired. Please log in again.');
          return;
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          setError('Your session has expired. Please log in again.');
          return;
        }
        const errorText = await response.text();
        throw new Error(`Failed to fetch sessions: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      const raw = data.sessions;

      if (Array.isArray(raw)) {
        applySessions(raw);
      } else {
        setSessions([]);
        setPinnedSessions([]);
        setUnpinnedSessions([]);
      }
    } catch (err) {
      setError('Failed to load chat sessions. Please try again later.');
      console.error('Error fetching chat sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveEmail, api_url, authHeader, refreshAccessToken]);

  const updateTitle = useCallback(async (id: number, title: string) => {
    if (!title.trim()) return;
    const response = await fetch(
      `${api_url}/api/v1/chat/sessions/${id}/title?title=${encodeURIComponent(title)}`,
      { method: 'PUT', headers: authHeader() }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update title: ${response.status} - ${errorText}`);
    }
    await refreshSessions();
  }, [api_url, authHeader, refreshSessions]);

  const deleteSession = useCallback(async (id: number) => {
    const response = await fetch(
      `${api_url}/api/v1/chat/sessions/${id}`,
      { method: 'DELETE', headers: authHeader() }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete chat: ${response.status} - ${errorText}`);
    }
    await refreshSessions();
  }, [api_url, authHeader, refreshSessions]);

  const togglePin = useCallback(async (id: number) => {
    if (!effectiveEmail) return;

    const session = sessions.find(s => s.id === id);
    if (!session) return;

    const isCurrentlyPinned = !!session.is_pinned;
    const now = new Date().toISOString();
    const newPinStatus = !isCurrentlyPinned;

    // Optimistic update
    const optimistic = sessions.map(s =>
      s.id === id
        ? { ...s, is_pinned: newPinStatus, pinned_at: newPinStatus ? now : null, updated_at: now }
        : s
    );
    applySessions(optimistic);

    try {
      const response = await fetch(
        `${api_url}/api/v1/chat/sessions/${id}/pin`,
        { method: 'PATCH', headers: authHeader() }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      await refreshSessions();
    } catch (err) {
      // Revert optimistic update
      applySessions(sessions);
      const msg = err instanceof Error ? err.message : 'Failed to update pin status. Please try again.';
      alert(msg);
    }
  }, [effectiveEmail, api_url, authHeader, sessions, refreshSessions]);

  // Initial fetch when user (or admin) becomes available
  useEffect(() => {
    if (effectiveEmail) {
      refreshSessions();
    }
  }, [effectiveEmail, refreshSessions]);

  return (
    <ChatSessionsContext.Provider
      value={{ sessions, pinnedSessions, unpinnedSessions, loading, error, refreshSessions, updateTitle, deleteSession, togglePin }}
    >
      {children}
    </ChatSessionsContext.Provider>
  );
};

export const useChatSessions = (): ChatSessionsContextValue => {
  const ctx = useContext(ChatSessionsContext);
  if (!ctx) throw new Error('useChatSessions must be used within ChatSessionsProvider');
  return ctx;
};
